import React from 'react';
import { Link, Tooltip } from "@mui/material";
import { useTable } from 'react-table';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Warning from '@mui/icons-material/WarningAmberOutlined';

import { getBaseUrl } from '../env';


export default function EmplAttachmentsTable({ attachments, employerId, onEditAttachment, onDeleteAttachment, role }) {
  const locale = Intl.NumberFormat().resolvedOptions().locale;

  // 1. Create Columns
  const columns = React.useMemo(() => {
    const getUTCDate = (value) => {
      if (!value) return '';
      const date = new Date(value);
      const utc = convertDateToUTC(date);
      return `${utc.toLocaleDateString(locale, {dateStyle:'medium'})}`;
    }

    const convertDateToUTC = (date) => {
      return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds());
    }

    const user = JSON.parse(localStorage.getItem('user'));
    const role = user.role || 'Recruiter';
    const isAdmin = role === 'Manager' || role === 'Administrator';

    return [{
       Header: 'Name',
       accessor: 'name',
       Cell: ({value, row}) => {
         if (!isAdmin) {
           const isInvoice = row.original.category === 'Invoice';
           if (isInvoice)
             return value;
         }
         const url = `${getBaseUrl()}/v1/employer/${employerId}/attachment/${row.original.id}/download`;
         return <Link href={url} target="_blank" rel="noreferrer" download underline="hover">{value}</Link>;
       }
     },
     {
       Header: 'Category',
       accessor: 'category',
       Cell: ({value, row}) => {
         let category = value;
         const invoice = row.original.invoice;
         if (invoice) {
           category += ` ${invoice}`;
         }
         return <>{category}</>;
       }
     },
     {
       Header: 'Notes',
       accessor: 'notes'
     },
     {
       Header: 'Terms',
       accessor: 'terms'
     },
     {
       Header: 'Executed',
       accessor: 'execution',
       Cell: ({value, row}) => {
         if (!value) return '--';
         return getUTCDate(value);
       }
     },
     {
       Header: 'Expiration',
       accessor: 'expiration',
       Cell: ({value, row}) => {
         if (!value) return '--';
         const { category, name } = row.original;
         const expiration = new Date(value);
         const expirationDate = getUTCDate(expiration);
         const now = Date.now();
         const twoWeeks = 2*7*24*60*60*1000;

         if (expiration.getTime() < now) {
           const msg = `${category || ''} ${name} expired on ${expirationDate}`;
           return <Tooltip title={msg} arrow><span style={{ display: 'flex', alignItems: 'center' }}><Warning sx={{color:'red'}}/> <strong>{expirationDate}</strong></span></Tooltip>;

         } else if (expiration.getTime() < (now + twoWeeks)) { 
           const msg = `${category || ''} ${name} will be expired on ${expirationDate}`;
           return <Tooltip title={msg} arrow><span style={{ display: 'flex', alignItems: 'center' }}><Warning sx={{color:'orange'}}/> <strong>{expirationDate}</strong></span></Tooltip>;
         } 
         return expirationDate;
       }
     }, {
       Header: 'Actions',
       Cell: ({row}) => {
         const isInvoice = row.original.category === 'Invoice';
         if (isAdmin) {
           return <div>
             <EditIcon onClick={(event) => onEditAttachment(event, row.original, row.index)} sx={{ cursor: 'pointer' }}/>
             <DeleteIcon onClick={(event) => onDeleteAttachment(event, row.original, row.index)} sx={{ cursor: 'pointer', ml: '0.5rem' }}/>
           </div>;

         } else if (isInvoice) {
           return '';

         } else {
           return <EditIcon onClick={(event) => onEditAttachment(event, row.original, row.index)} sx={{ cursor: 'pointer' }}/>;
         }
       }
      }];
    }, [employerId, onEditAttachment, onDeleteAttachment, locale]
  );

  // 2. Call Use Table
  const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow
    } = useTable({ columns, data: attachments });

    // 3. Return the HTML Table
    return (<>
        <table {...getTableProps()}>
            <thead>
                {headerGroups.map(headerGroup => {
                    const { key, ...restOfRowProps } = headerGroup.getHeaderGroupProps();
                    return <tr key={key} {...restOfRowProps}>
                        {headerGroup.headers.map(column => {
                            // Add the sorting props to control sorting. For this example
                            // we can add them into the header props
                            const { key, ...restOfColumnProps } = column.getHeaderProps();
                            return <th key={key} {...restOfColumnProps}>
                                {column.render('Header')}
                            </th>
                        })}
                    </tr>
                })}
            </thead>
            <tbody {...getTableBodyProps()}>
                {rows.map(
                    (row) => {
                        prepareRow(row);
                        const { key, ...restOfRowProps } = row.getRowProps();
                        return (
                            <tr key={key} {...restOfRowProps}>
                                {row.cells.map(cell => {
                                    const { key, ...restOfCellProps } = cell.getCellProps();
                                    return (
                                        <td key={key} {...restOfCellProps}>{cell.render('Cell')}</td>
                                    );
                                })}
                            </tr>
                        );
                    }
                )}
            </tbody>
        </table>

        { attachments && attachments.length === 0 &&
          <p className="empty-message">No Attachments</p>
        }
    </>);
}
 
